<template>
    <div v-if="item" class="item">
        <page-header :title="item.model">
            <button class="button is-small mr-2" @click.prevent="toggleAdminFields" v-if="isAdmin">
                <span class="icon is-small">
                    <i v-if="showAdminFields" class="fas fa-eye-slash"/>
                    <i v-else class="fas fa-eye"/>
                </span>
            </button>
            <button class="button is-small mr-2"  title="Rediger" @click="openItemModal()">
                <span class="icon is-small">
                    <i class="fas fa-edit"/>
                </span>
            </button>
            <button class="button is-small mr-2 is-relative"  title="Dokumenter" @click="openDocumentModal()">
                <span class="icon is-small">
                    <i class="fas fa-paperclip"/>
                </span>
                <div>
                    {{ item.documents.length }}
                </div>
            </button>
            <button class="button is-small"  title="Opret kopi" @click="openItemCopyModal()">
                <span class="icon is-small">
                    <i class="fas fa-copy"/>
                </span>
            </button>
        </page-header>

        <hr class="contacts-hr"/>

        <div class="columns">
            <div class="column is-6">
                <Gallery
                    :item="item"
                    :images="item.images"
                    @delete="onDeleteImage"
                    @imagesUploaded="onUploudImages">
                    <ItemDescriptions :item="item"/>
                </Gallery>
            </div>
            <div class="column is-6">
                <ItemMasterData
                    :item="item"
                    :show-admin-fields="showAdminFields"
                    class="display-data"
                    @showHistory="openCostPriceHistoryModal"
                    @updated="onItemUpdated"
                    @confirmCostPrice="onConfirmCostPrice"
                />
                <Prices
                    :item="item"
                    :show-admin-fields="showAdminFields"
                    :is-admin="isAdmin"
                    class="display-data"
                    @updated="onPricesUpdated"/>
                <ItemStock
                    v-if="item.type.real_item"
                    :item="item"
                    :show-admin-fields="showAdminFields"
                    class="display-data"
                    @updated="onItemStockUpdated"/>
            </div>
        </div>

        <ItemModal
            v-if="itemModalOpen"
            :active="itemModalOpen"
            :item="item"
            :viaList="false"
            @closed="itemModalClosed"
            @updated="onItemUpdated"
            @showHistory="openCostPriceHistoryModal"
            @confirmCostPrice="onConfirmCostPrice"
        />

        <ItemDocumentModal
            :active="documentModalOpen"
            :item="item"
            @closed="closeDocumentModal"
            @updated="onItemUpdated"
        />

        <SerialIndex
            class="serial-index-margin"
            v-if="item.type.key === 'with_serial'"
            :item="item"
            :showAdminFields="showAdminFields"
            @updated="onSerialUpdated"
        />

        <PriceHistoryModal
            :active="showPriceHistoryModal" 
            :item="item" 
            @closed="onCloseHistoryModal" />

        <ItemCopyModal
            :active="itemCopyModalOpen"
            :item="item"
            @closed="closeItemCopyModal" />
    </div>
</template>

<script>
import PageHeader from '@elems/pageHeader'
import List from '@elems/list'
import ItemModal from './forms/ItemModal'
import Gallery from "@elems/Gallery";
import ItemDescriptions from "./show/ItemDescriptions";
import ItemMasterData from "./show/ItemMasterData";
import Prices from "@elems/prices/Index";
import ItemStock from "./show/ItemStock";
import SerialIndex from '../serials/Index';
import {mapGetters} from "vuex";
import PriceHistoryModal from "@elems/prices/show/PriceHistoryModal";
import ItemDocumentModal from "./forms/ItemDocumentModal";
import ItemCopyModal from "./forms/ItemCopyModal";

export default {
    components: {
        ItemStock,
        Prices,
        ItemMasterData,
        ItemDescriptions,
        Gallery,
        PageHeader,
        List,
        ItemModal,
        SerialIndex,
        PriceHistoryModal,
        ItemDocumentModal,
        ItemCopyModal
    },
    props: {
        id: [String, Number],
    },
    data() {
        return {
            itemModalOpen: false,
            itemCopyModalOpen: false,
            documentModalOpen: false,
            item: null,
            showAdminFields: false,
            showPriceHistoryModal: false,
            selectedPrice: null,
        };
    },
    computed: {
        ...mapGetters(['isAdmin'])
    },
    methods: {
        onConfirmCostPrice(price) {
            if (!confirm('Er du sikker på at du vil bekræfte kostprisen?')) {
                return
            }

            window.axios.post('/api/items/' + this.item.id + '/cost-price/confirm')
                .then((response) => {
                    this.item.cost_price.outdated = false;
                })
                .catch(error => {
                    console.error(error)
                });
        },
        openItemCopyModal() {
            this.itemCopyModalOpen = true;
        },
        closeItemCopyModal() {
            this.itemCopyModalOpen = false;
        },
        openDocumentModal() {
            this.documentModalOpen = true;
        },
        closeDocumentModal() {
            this.documentModalOpen = false;
        },
        openCostPriceHistoryModal() {
            this.showPriceHistoryModal = true;
        },
        onCloseHistoryModal() {
            this.showPriceHistoryModal = false;
        },
        openItemModal() {
            this.itemModalOpen = true
        },
        itemModalClosed() {
            this.itemModalOpen = false
        },
        closeItemModal() {
            this.itemModalOpen = false
        },
        onUploudImages(images) {
            this.item.images = this.item.images.concat(images);
        },
        onDeleteImage({ id }) {
            this.deleteImage(id);
        },
        deleteImage(id) {
            window.axios.delete('/api/images/' + id)
                .then(() => {
                    this.item.images = this.item.images.filter(img => img.id !== id);
                    this.$store.dispatch('setSuccess', 'Billedet blev slettet')
                })
                .catch(error => {
                    console.error(error)
                    this.$store.dispatch('setError', 'Der opstod en fejl')
                });
        },
        toggleAdminFields() {
            this.showAdminFields = !this.showAdminFields;
        },
        onItemUpdated() {
            this.loadItem();
        },
        onSerialUpdated() {
            this.loadItem();
        },
        onPricesUpdated() {
            this.loadItem();
        },
        onItemStockUpdated() {
            this.loadItem();
        },
        loadItem() {
            window.axios.get('/api/items/' + this.id)
                .then((response) => {
                    this.item = response.data;
                })
                .catch(error => {
                    console.error(error)
                });
        },
    },
    created() {
        this.$store.dispatch('loadLocales');
        this.$store.dispatch('loadDocumentGroups');
        this.$store.dispatch('loadItemCategories');
        this.loadItem();
    }
}
</script>

<style lang="scss" scoped>
.contacts-hr {
    margin-top: 40px;
}
.item {
    padding-bottom: 300px;
}
.serial-index-margin {
    margin-top: 3rem;

    @media (min-width: 1024px) {
        margin-left: 4.25rem;
    }
}
</style>
